import React, { useState, useEffect } from 'react';
import accessLogsService from '../../../services/accessLogsService';
import LogTable from './LogTable';
import SummaryMetrics from './SummaryMetrics';
import FilterPanel from './FilterPanel';

const LoginHistoryTab = ({ darkMode }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 50,
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadData();
  }, [filters]);

  useEffect(() => {
    const handleRefresh = () => {
      loadData();
    };
    window.addEventListener('accessLogsRefresh', handleRefresh);
    return () => window.removeEventListener('accessLogsRefresh', handleRefresh);
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [logsData, summaryData] = await Promise.all([
        accessLogsService.getLoginHistory(filters),
        accessLogsService.getSummary()
      ]);
      setLogs(logsData.logs || []);
      setTotalCount(logsData.totalCount || 0);
      setSummary(summaryData);
    } catch (err) {
      setError(err.message || 'Failed to load login history');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleSort = (column, order) => {
    setFilters({ ...filters, sortBy: column, sortOrder: order, page: 1 });
  };

  const columns = [
    { key: 'timestamp', label: 'Timestamp', sortable: true },
    { key: 'user', label: 'User', sortable: false },
    { key: 'ipAddress', label: 'IP Address', sortable: true },
    { key: 'deviceInfo', label: 'Device', sortable: false },
    { key: 'loginMethod', label: 'Method', sortable: false },
    { key: 'sessionDuration', label: 'Duration', sortable: false },
    { key: 'status', label: 'Status', sortable: false }
  ];

  const formatLogRow = (log) => ({
    id: log.id,
    timestamp: new Date(log.timestamp).toLocaleString(),
    user: log.user ? `${log.user.firstName || ''} ${log.user.lastName || ''}`.trim() || log.user.email : log.email || 'N/A',
    ipAddress: log.ipAddress || 'N/A',
    deviceInfo: log.deviceInfo || 'N/A',
    loginMethod: log.loginMethod || 'N/A',
    sessionDuration: log.sessionDuration ? `${Math.floor(log.sessionDuration / 60)}m ${log.sessionDuration % 60}s` : 'N/A',
    status: log.status || 'N/A'
  });

  return (
    <div className={`access-logs-tab-content ${darkMode ? 'dark-mode' : ''}`}>
      {summary && <SummaryMetrics metrics={summary} darkMode={darkMode} />}
      <FilterPanel filters={filters} onChange={handleFilterChange} darkMode={darkMode} />
      {error && (
        <div className="error-message">
          {error}
          <button onClick={loadData}>Retry</button>
        </div>
      )}
      <LogTable
        columns={columns}
        data={logs.map(formatLogRow)}
        loading={loading}
        pagination={{
          currentPage: filters.page,
          pageSize: filters.pageSize,
          totalCount: totalCount,
          onPageChange: handlePageChange
        }}
        sorting={{
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          onSort: handleSort
        }}
        darkMode={darkMode}
      />
    </div>
  );
};

export default LoginHistoryTab;

